<script setup lang="ts">
import { Icon } from '@iconify/vue';
import { computed, nextTick, onMounted, ref } from 'vue';

const props = withDefaults(defineProps<{
  mode?: 'login' | 'bind';
}>(), {
  mode: 'login',
});

declare global {
  interface Window {
    WxLogin?: (options: {
      self_redirect: boolean;
      id: string;
      appid: string;
      scope: string;
      redirect_uri: string;
      state: string;
      style?: 'black' | 'white';
      href?: string;
    }) => unknown;
  }
}

const WECHAT_LOGIN_SCRIPT = 'https://res.wx.qq.com/connect/zh_CN/htmledition/js/wxLogin.js';
const WECHAT_APP_ID = import.meta.env.VITE_WECHAT_APP_ID || 'wx56ecc05a2b14a0ba';

const qrContainerId = `wechat-login-${Math.random().toString(36).slice(2)}`;
const isScriptReady = ref(false);
const isFailed = ref(false);

const state = computed(() => `${props.mode}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
const redirectUri = computed(() => encodeURIComponent(`${window.location.origin}/wechat/callback?mode=${props.mode}`));
const title = computed(() => props.mode === 'bind' ? '绑定微信账号' : '微信扫码登录');
const description = computed(() => props.mode === 'bind' ? '绑定后可直接用微信扫码进入学习空间' : '打开微信扫一扫，可登录或创建微信临时账号');

function loadWechatScript() {
  if (window.WxLogin) {
    isScriptReady.value = true;
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${WECHAT_LOGIN_SCRIPT}"]`);
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('微信登录脚本加载失败')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = WECHAT_LOGIN_SCRIPT;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('微信登录脚本加载失败'));
    document.head.appendChild(script);
  });
}

function renderWechatQr() {
  if (!window.WxLogin)
    throw new Error('微信登录组件未就绪');

  window.WxLogin({
    self_redirect: false,
    id: qrContainerId,
    appid: WECHAT_APP_ID,
    scope: 'snsapi_login',
    redirect_uri: redirectUri.value,
    state: state.value,
    style: 'black',
  });
}

onMounted(async () => {
  try {
    await loadWechatScript();
    isScriptReady.value = true;
    await nextTick();
    renderWechatQr();
  }
  catch (e) {
    console.error('Wechat login init failed:', e);
    isFailed.value = true;
  }
});
</script>

<template>
  <section class="h-full rounded-2xl border border-primary/10 bg-gradient-to-b from-primary/5 via-white to-white p-5 shadow-sm">
    <div class="mb-4 flex items-start gap-3">
      <div class="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#07c160]/10 text-[#07c160]">
        <Icon icon="mdi:wechat" class="size-6" />
      </div>
      <div>
        <h2 class="text-lg font-semibold text-neutral-900">
          {{ title }}
        </h2>
        <p class="mt-1 text-sm leading-6 text-neutral-500">
          {{ description }}
        </p>
      </div>
    </div>

    <div class="flex min-h-[318px] items-center justify-center rounded-xl border border-dashed border-neutral-200 bg-white">
      <div v-if="isFailed" class="px-6 text-center text-sm leading-6 text-neutral-500">
        二维码加载失败，请刷新页面后重试
      </div>
      <div v-else-if="!isScriptReady" class="flex flex-col items-center gap-3 text-sm text-neutral-500">
        <Icon icon="lucide:loader-2" class="size-6 animate-spin text-primary" />
        正在生成二维码
      </div>
      <div :id="qrContainerId" class="wechat-qr-frame" />
    </div>

    <div class="mt-4 rounded-xl bg-primary/5 px-4 py-3 text-sm leading-6 text-neutral-600">
      <template v-if="mode === 'bind'">
        微信仅用于身份验证；账号信息仍由邮趣数学统一管理。
      </template>
      <template v-else>
        已绑定微信的用户会直接登录；首次扫码会创建微信账号，之后可在头像菜单中绑定北邮统一认证。
      </template>
    </div>
  </section>
</template>

<style scoped>
.wechat-qr-frame :deep(iframe) {
  width: 300px;
  height: 300px;
}
</style>
